
import React, { useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  createRazorpayOrder,
  verifyRazorpayPayment,
} from "../../actions/paymentActions";

import { useCart } from "../../components/CartContext";
import { useRestaurantSession } from "../../components/RestaurantSessionProvider";

import "./css/PaymentScreen.css";

function PaymentScreen() {
  const { restaurantId, tableId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { sessionToken } = useRestaurantSession();
  const { clearCart } = useCart();

  const { restaurantName } = useSelector(
    (state) => state.theme || {}
  );

  const createdOrder = useSelector(
    (state) => state.order?.order
  );

  const {
    loading: paymentLoading,
    error: paymentError,
  } = useSelector((state) => state.payment || {});

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  const basePath = `/${restaurantId}/table/${tableId}`;

  const orderId =
    createdOrder?.id || location.state?.orderId;

  const total = Number(
    createdOrder?.total ?? location.state?.total ?? 0
  );

  const formatPrice = (value) =>
    `₹${Number(value ?? 0).toFixed(2)}`;

  // Load Razorpay SDK when payment is requested.
  const loadRazorpay = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const existingScript = document.querySelector(
        'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
      );

      if (existingScript) {
        existingScript.addEventListener(
          "load",
          () => resolve(true),
          { once: true }
        );

        existingScript.addEventListener(
          "error",
          () => resolve(false),
          { once: true }
        );

        return;
      }

      const script = document.createElement("script");
      script.src =
        "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;

      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);

      document.body.appendChild(script);
    });
  };

  const handleBack = () => {
    if (processing) return;

    navigate(`${basePath}/order`);
  };

  const handlePayment = async () => {
    if (processing || paymentLoading) return;

    setError("");

    if (!orderId) {
      setError("Order information is missing.");
      return;
    }

    if (!sessionToken) {
      setError(
        "Dining session is not ready. Please scan the table QR code again."
      );
      return;
    }

    setProcessing(true);

    try {
      const razorpayLoaded = await loadRazorpay();

      if (!razorpayLoaded || !window.Razorpay) {
        throw new Error(
          "Unable to load Razorpay. Please check your internet connection."
        );
      }

      // IMPORTANT: Pass sessionToken as the third argument.
      const razorpayData = await dispatch(
        createRazorpayOrder(
          restaurantId,
          orderId,
          sessionToken
        )
      );

      const keyId = razorpayData?.key_id;
      const razorpayOrderId =
        razorpayData?.razorpay_order_id;
      const amount = Number(razorpayData?.amount);
      const currency = razorpayData?.currency;

      if (!keyId || !razorpayOrderId) {
        throw new Error(
          "Payment details were not returned by the server."
        );
      }

      if (
        !Number.isFinite(amount) ||
        amount <= 0 ||
        !currency
      ) {
        throw new Error(
          "Invalid payment amount or currency returned by the server."
        );
      }

      const paymentRestaurantName =
        razorpayData?.restaurant_name ||
        restaurantName ||
        "Restaurant";

      const options = {
        key: keyId,
        amount,
        currency,
        name: paymentRestaurantName,
        description: `Order #${orderId}`,
        order_id: razorpayOrderId,

        theme: {
          color: "#b99a5b",
        },

        handler: async function (response) {
          try {
            if (
              !response?.razorpay_order_id ||
              !response?.razorpay_payment_id ||
              !response?.razorpay_signature
            ) {
              throw new Error(
                "Incomplete Razorpay payment response."
              );
            }

            // IMPORTANT: Pass sessionToken during verification.
            await dispatch(
              verifyRazorpayPayment(
                restaurantId,
                {
                  order_id: orderId,
                  razorpay_order_id:
                    response.razorpay_order_id,
                  razorpay_payment_id:
                    response.razorpay_payment_id,
                  razorpay_signature:
                    response.razorpay_signature,
                },
                sessionToken
              )
            );

            // Only clear the cart after backend verification.
            clearCart();

            navigate(`${basePath}/success`, {
              replace: true,
              state: {
                orderId,
                paymentMethod: "ONLINE",
                total,
              },
            });
          } catch (verifyError) {
            setError(
              "Payment verification could not be completed. " +
              "Please check your payment status before trying again."
            );

            setProcessing(false);
          }
        },

        modal: {
          ondismiss: function () {
            setProcessing(false);
          },
        },

        prefill: {
          name: "",
          email: "",
          contact: "",
        },

        notes: {
          order_id: String(orderId),
          restaurant_id: String(restaurantId),
          table_id: String(tableId),
        },

        retry: {
          enabled: true,
          max_count: 2,
        },
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on("payment.failed", function (response) {
        setError(
          response?.error?.description ||
          "Payment failed. Please check your payment status before retrying."
        );

        setProcessing(false);
      });

      razorpay.open();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Unable to start payment."
      );

      setProcessing(false);
    }
  };

  if (!orderId) {
    return (
      <div className="theme4-payment-page">
        <div className="theme4-payment-container">
          <div className="theme4-payment-error-page">
            <div className="theme4-payment-error-icon">
              !
            </div>

            <span>PAYMENT</span>
            <h1>PAYMENT UNAVAILABLE</h1>

            <p>
              We could not find the order required to
              start payment.
            </p>

            <button
              type="button"
              className="theme4-payment-primary-button"
              onClick={() => navigate(basePath)}
            >
              RETURN TO MENU
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="theme4-payment-page">
      <div className="theme4-payment-container">
        <button
          type="button"
          className="theme4-payment-back"
          onClick={handleBack}
          disabled={processing}
        >
          <span>←</span> BACK TO ORDER
        </button>

        <div className="theme4-payment-heading">
          <div className="theme4-payment-heading-top">
            <span className="theme4-payment-eyebrow">
              SECURE PAYMENT
            </span>

            <span className="theme4-payment-step">
              03
            </span>
          </div>

          <h1>PAYMENT</h1>
          <p>
            Complete your payment securely to confirm
            your order.
          </p>
        </div>

        <div className="theme4-payment-layout">
          <main className="theme4-payment-main">
            <section className="theme4-payment-method-card">
              <div className="theme4-payment-method-icon">
                ◇
              </div>

              <div className="theme4-payment-method-content">
                <span>PAYMENT METHOD</span>
                <h2>ONLINE PAYMENT</h2>

                <p>
                  You will be securely redirected to
                  Razorpay to complete your payment.
                </p>
              </div>

              <div className="theme4-payment-secure-badge">
                SECURE
              </div>
            </section>

            <section className="theme4-payment-information">
              <div className="theme4-payment-information-heading">
                <span>ORDER DETAILS</span>
                <small>03</small>
              </div>

              <div className="theme4-payment-information-row">
                <span>RESTAURANT</span>
                <strong>
                  {restaurantName || "Restaurant"}
                </strong>
              </div>

              <div className="theme4-payment-information-row">
                <span>ORDER</span>
                <strong>#{orderId}</strong>
              </div>

              <div className="theme4-payment-information-row">
                <span>PAYMENT</span>
                <strong>ONLINE</strong>
              </div>

              <div className="theme4-payment-information-row">
                <span>CURRENCY</span>
                <strong>INR ₹</strong>
              </div>
            </section>

            {(error || paymentError) && (
              <div
                className="theme4-payment-error"
                role="alert"
              >
                <span>!</span>
                <p>
                  {error ||
                    (typeof paymentError === "string"
                      ? paymentError
                      : "Payment could not be started.")}
                </p>
              </div>
            )}

            <button
              type="button"
              className="theme4-payment-pay-button"
              onClick={handlePayment}
              disabled={
                processing ||
                paymentLoading ||
                !sessionToken
              }
            >
              {processing || paymentLoading ? (
                <>
                  <span className="theme4-payment-spinner" />
                  PROCESSING...
                </>
              ) : !sessionToken ? (
                <>DINING SESSION UNAVAILABLE</>
              ) : (
                <>
                  PAY {formatPrice(total)}
                  <span>→</span>
                </>
              )}
            </button>

            <div className="theme4-payment-security">
              <span>◈</span>
              <p>
                Payments are processed securely through
                Razorpay. Your card or banking details are
                handled by the payment gateway.
              </p>
            </div>
          </main>

          <aside className="theme4-payment-summary">
            <div className="theme4-payment-summary-heading">
              <div>
                <span>ORDER SUMMARY</span>
                <h2>PAYMENT</h2>
              </div>
              <small>FINAL</small>
            </div>

            <div className="theme4-payment-summary-row">
              <span>ORDER NUMBER</span>
              <strong>#{orderId}</strong>
            </div>

            <div className="theme4-payment-summary-row">
              <span>RESTAURANT</span>
              <strong>
                {restaurantName || "Restaurant"}
              </strong>
            </div>

            <div className="theme4-payment-summary-row">
              <span>PAYMENT METHOD</span>
              <strong>ONLINE</strong>
            </div>

            <div className="theme4-payment-summary-divider" />

            <div className="theme4-payment-total">
              <div>
                <span>AMOUNT TO PAY</span>
                <small>INCLUDING ALL CHARGES</small>
              </div>

              <strong>{formatPrice(total)}</strong>
            </div>

            <div className="theme4-payment-summary-note">
              <span>✦</span>
              <p>
                Your order will be confirmed after
                successful payment verification.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default PaymentScreen;
